<template>
  <div>
    <select-field ref="RibSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Rib from '@/extend/classes/Rib';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionRibExtend from '@/extend/sections/SectionRibExtend.vue';
  import { storeRib } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listRib: Rib[] = [];
      const tmpRib: Rib = new Rib();
      return {
        selectedRibId: 0,
        selectedRib: new Rib(),
        tmpRib,
        newContext: true,
        reset: true,
        listUpdated: false,
        listRib
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionRibExtend;
        section.type = 'rib';
        section.data = this.selectedRib;
        section.picto = 'SectionRibExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Rib[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listRib;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau compte bancaire")); 
        const ribs = this.getItems(this.listItems, this.sectionName);
        ribs.forEach(rib => {
          if (rib && rib.id > 0 ) {
            items.push(new Item(rib.id, this.getDisplay(rib)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const rib = this.listRib.find(a => a.id == itemDefined.key);
          if (rib && rib.id != this.selectedRibId) {
            this.setSelectedRib(rib);
          }
          if (item.key == 0 && this.selectedRibId == 0) {
            this.setSelectedRib(this.newRib());
          }
          this.setSelectedRibId(rib ? rib.id : this.selectedRib.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedRib, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedRib(value: Rib) {
        this.selectedRib = value;
      },
      setSelectedRibId(id: number) {
        this.selectedRibId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newRib (): Rib {
        this.clearStore();
        const newRib = storeRib.createNewRib();
        this.tmpRib = newRib;
        return newRib;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Rib.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Rib[];
                this.listRib = [];
                items.forEach((item) => {
                  this.listRib.push(Rib.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpRib != null && this.tmpRib.id < 0) {
          storeRib.mRibDeleted(this.tmpRib.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedRibId = 0;
        this.selectedRib = new Rib();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(rib: Rib[], section: String): Rib[] {
        const items: Rib[] = [];
        rib.forEach((item) => {
          items.push(Rib.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(rib: Rib): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(rib: Rib, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(rib: Rib) {
        const item = this.items.find(a => a.key == rib.id);
        if (!item && rib.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == rib.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedRib = this.newRib();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (rib: Rib) {
        this.selectedRib = rib;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.RibSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.RibSummary.touch();
      }
    },
  })
</script>
